/**
 * StatusBar — barra de estado map-céntrica (primitiva S.UI.P2).
 *
 * Presentacional puro: sin lógica, estado ni reproyección. Recibe coordenadas YA
 * en EPSG:25830 y solo las formatea (sin separador de miles, convención QGIS UTM).
 * Muestra coords en vivo (cursor/selección), contadores (ubicadas/pendientes) y un
 * botón de expandir tabla. Para la ranura `barraEstado` de `WorkspaceShell`
 * (S.UI.P1); no se monta aún (DoD por primitiva). Skin DS v2 (textos en Space
 * Grotesk, cifras en JetBrains Mono); sin glow/box-shadow (regla dura). La
 * reproyección WGS84/3857 → 25830 vive en el consumidor (p. ej. `src/lib/leaflet-crs`).
 *
 * @version 1.0.0
 */

import type { CSSProperties, FC } from 'react';
import { colors, typography } from '@/design-system/v2';

// ── Tokens DS v2 ─────────────────────────────────────────────────────────────
const COLORS = {
  bg: colors.bg.tertiary,
  fg: colors.fg.secondary,
  fgFuerte: colors.fg.primary,
  fgTenue: colors.fg.muted,
  borde: colors.border.DEFAULT,
  ubicadas: colors.success, // #a3e635 — «ya ubicadas»
  pendientes: colors.warning, // #fbbf24 — «pendientes» / «por revisar»
  sinCoordenada: colors.error, // #f87171 — «sin coordenada»
  etiqueta: colors.primary, // #2dd4bf — acento del CRS
} as const;

const DECIMALES_DEF = 2;

// ── Tipos ────────────────────────────────────────────────────────────────────
/** Par de coordenadas en EPSG:25830 (UTM30 ETRS89), en metros. */
export interface Coord25830 {
  x: number; // Este (X), metros
  y: number; // Norte (Y), metros
}

export interface StatusBarProps {
  /** Coordenada del cursor sobre el mapa (EPSG:25830); ausente si no hay cursor. */
  coordCursor?: Coord25830;
  /** Coordenada de la entidad seleccionada (EPSG:25830); ausente si no hay selección. */
  coordSeleccion?: Coord25830;
  /** Nº de infraestructuras ya ubicadas. Def: 0. */
  nUbicadas?: number;
  /** Nº de infraestructuras pendientes de ubicar. Def: 0. Solo P3, hasta `P2VOC-c`. */
  nPendientes?: number;
  /**
   * P2VOC-b: filas «Por revisar» y «Sin coordenada» (`shared/estadoFila`). Si
   * llega alguno de los dos, la barra pinta los tres estados y no «pendientes».
   */
  nPorRevisar?: number;
  nSinCoordenada?: number;
  /** Decimales de X/Y (sin separador de miles, convención QGIS). Def: 2. */
  decimales?: number;
  /** Callback de «Expandir tabla»; si se omite, el botón no se muestra. */
  onExpandirTabla?: () => void;
  /** Clase CSS adicional. */
  className?: string;
}

// ── Formato (presentación pura, sin separador de miles) ──────────────────────
const formatearValor = (v: number, decimales: number): string => v.toFixed(decimales);

// ── Estilos ──────────────────────────────────────────────────────────────────
const raizStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px',
  width: '100%', minHeight: 0, padding: '4px 12px', overflow: 'hidden', whiteSpace: 'nowrap',
  background: COLORS.bg, color: COLORS.fg, fontFamily: typography.fontFamily.sans,
  fontSize: typography.fontSize.xs, lineHeight: typography.lineHeight.tight,
};
const grupoStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 };
const etiquetaCrsStyle: CSSProperties = {
  color: COLORS.etiqueta, fontFamily: typography.fontFamily.mono,
  fontWeight: typography.fontWeight.semibold, letterSpacing: typography.letterSpacing.wide,
};
const rotuloStyle: CSSProperties = { color: COLORS.fgTenue };
const cifraStyle: CSSProperties = { fontFamily: typography.fontFamily.mono, color: COLORS.fgFuerte };
const contadorStyle = (color: string): CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: '4px', color,
});
const numContadorStyle: CSSProperties = {
  fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.semibold,
};
const botonStyle: CSSProperties = {
  appearance: 'none', border: `1px solid ${COLORS.borde}`, borderRadius: '6px',
  background: 'transparent', color: COLORS.fg, fontFamily: typography.fontFamily.sans,
  fontSize: typography.fontSize.xs, padding: '2px 10px', cursor: 'pointer', // sin glow (regla dura)
};

// ── Subcomponente: bloque de coordenadas (rótulo + X + Y en mono) ────────────
const BloqueCoord: FC<{ testid: string; rotulo: string; coord: Coord25830; decimales: number }> = ({
  testid, rotulo, coord, decimales,
}) => (
  <span data-testid={testid} style={grupoStyle}>
    <span style={rotuloStyle}>{rotulo}</span>
    <span style={cifraStyle} data-testid={`${testid}-x`}>X {formatearValor(coord.x, decimales)}</span>
    <span style={cifraStyle} data-testid={`${testid}-y`}>Y {formatearValor(coord.y, decimales)}</span>
  </span>
);

// ── Componente ───────────────────────────────────────────────────────────────
export const StatusBar: FC<StatusBarProps> = ({
  coordCursor, coordSeleccion, nUbicadas = 0, nPendientes = 0, nPorRevisar, nSinCoordenada,
  decimales = DECIMALES_DEF, onExpandirTabla, className,
}) => {
  const tresEstados = nPorRevisar != null || nSinCoordenada != null;
  const claseRaiz = className ? `ptel-status-bar ${className}` : 'ptel-status-bar';
  return (
    <div className={claseRaiz} data-testid="status-bar" style={raizStyle}>
      {/* Izquierda: CRS + coordenadas en vivo */}
      <div style={grupoStyle}>
        <span data-testid="status-bar-epsg" style={etiquetaCrsStyle}>EPSG:25830</span>
        {coordCursor != null && (
          <BloqueCoord testid="status-bar-cursor" rotulo="Cursor" coord={coordCursor} decimales={decimales} />
        )}
        {coordSeleccion != null && (
          <BloqueCoord testid="status-bar-seleccion" rotulo="Selección" coord={coordSeleccion} decimales={decimales} />
        )}
      </div>
      {/* Derecha: contadores + botón expandir */}
      <div style={grupoStyle}>
        <span data-testid="status-bar-ubicadas" style={contadorStyle(COLORS.ubicadas)}>
          <span style={numContadorStyle}>{nUbicadas}</span>
          {tresEstados && nUbicadas === 1 ? ' ubicada' : ' ubicadas'}
        </span>
        {tresEstados ? (
          <>
            <span data-testid="status-bar-por-revisar" style={contadorStyle(COLORS.pendientes)}>
              <span style={numContadorStyle}>{nPorRevisar ?? 0}</span> por revisar
            </span>
            <span data-testid="status-bar-sin-coordenada" style={contadorStyle(COLORS.sinCoordenada)}>
              <span style={numContadorStyle}>{nSinCoordenada ?? 0}</span> sin coordenada
            </span>
          </>
        ) : (
          <span data-testid="status-bar-pendientes" style={contadorStyle(COLORS.pendientes)}>
            <span style={numContadorStyle}>{nPendientes}</span> pendientes
          </span>
        )}
        {onExpandirTabla != null && (
          <button type="button" data-testid="status-bar-expandir" style={botonStyle} onClick={onExpandirTabla}>
            Expandir tabla
          </button>
        )}
      </div>
    </div>
  );
};

export default StatusBar;
